'use client';

/**
 * CWV DevTools Overlay
 *
 * Real-time Core Web Vitals monitoring during development.
 * Shows CLS, LCP, and INP scores in a floating panel.
 *
 * Uses ENVIRONMENT variable (local, dev, qa, stage, prod)
 *
 * Shows on:
 *   - ENVIRONMENT=local
 *   - ENVIRONMENT=dev
 *   - ENVIRONMENT=qa
 *
 * Hidden on:
 *   - ENVIRONMENT=stage
 *   - ENVIRONMENT=prod
 *
 * NOTE: Inline styles are used (instead of Tailwind) for compatibility with both
 * page router and app router.
 *
 * Why this exists (vs browser DevTools):
 *   - Always visible during development without opening DevTools
 *   - Highlights actual DOM elements causing CLS shifts
 *   - Proactive notifications when CLS exceeds thresholds (important for SEO)
 *   - Team-wide consistency across all engineers
 *   - Environment-aware (auto-hides in prod/stage)
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CLSMetricWithAttribution, INPMetricWithAttribution, LCPMetricWithAttribution } from 'web-vitals';
import { onCLS, onINP, onLCP } from 'web-vitals/attribution';

// Core Web Vitals thresholds (Google's official thresholds)
const cwvThresholds = {
  clsGood: 0.1,
  clsNeedsImprovement: 0.25,
  inpGood: 200,
  inpNeedsImprovement: 500,
  lcpGood: 2500,
  lcpNeedsImprovement: 4000,
} as const;

// Display precision for CLS values
const clsDisplayPrecision = 3;

// Highlight duration for CLS elements (ms)
const clsHighlightDuration = 10000;

// Fixed position for the panel
const PANEL_POSITION = { x: 16, y: 16 };

/**
 * Check if we should show the CWV overlay
 * Uses ENVIRONMENT variable: local, dev, qa, stage, prod
 * Shows on: local, dev, qa
 * Hidden on: stage, prod
 */
function shouldShowOverlay() {
  const env = process.env.ENVIRONMENT?.toLowerCase();

  // Show on local, dev, qa
  // Hide on stage, prod (or if ENVIRONMENT is not set)
  return env === 'local' || env === 'dev' || env === 'qa';
}

/**
 * Get current environment for display
 */
function getCurrentEnv() {
  return process.env.ENVIRONMENT || 'LOCAL';
}

/**
 * Check if current environment is local
 */
function isLocalEnv() {
  return process.env.ENVIRONMENT?.toLowerCase() === 'local';
}

interface CwvData {
  cls: number;
  lcp: number;
  inp: number;
  clsRating: 'good' | 'needs-improvement' | 'poor';
  lcpRating: 'good' | 'needs-improvement' | 'poor';
  inpRating: 'good' | 'needs-improvement' | 'poor';
  shifts: Array<{ element: string; value: number }>;
}

type Rating = 'good' | 'needs-improvement' | 'poor';

function getRating(metric: 'cls' | 'lcp' | 'inp', value: number): Rating {
  if (metric === 'cls') {
    if (value <= cwvThresholds.clsGood) {
      return 'good';
    }
    if (value <= cwvThresholds.clsNeedsImprovement) {
      return 'needs-improvement';
    }
    return 'poor';
  }
  if (metric === 'lcp') {
    if (value <= cwvThresholds.lcpGood) {
      return 'good';
    }
    if (value <= cwvThresholds.lcpNeedsImprovement) {
      return 'needs-improvement';
    }
    return 'poor';
  }
  // inp
  if (value <= cwvThresholds.inpGood) {
    return 'good';
  }
  if (value <= cwvThresholds.inpNeedsImprovement) {
    return 'needs-improvement';
  }
  return 'poor';
}

const colors: Record<Rating, string> = {
  good: '#0cce6b',
  'needs-improvement': '#ffa400',
  poor: '#ff4e42',
};

// localStorage keys
const STORAGE_KEY_VISIBLE = 'cwv-overlay-visible';
const STORAGE_KEY_HIGHLIGHT = 'cwv-overlay-highlight';

// SSR default is closed to avoid hydration mismatch
const SSR_DEFAULT_VISIBLE = false;

// Client default (when no localStorage value exists) is open
const CLIENT_DEFAULT_VISIBLE = true;

// CLS highlighting is on by default
const DEFAULT_HIGHLIGHT = true;

/**
 * Get stored visibility preference from localStorage
 * Returns true (open) unless user has explicitly closed it before
 */
function getStoredVisible() {
  if (typeof window === 'undefined') {
    return SSR_DEFAULT_VISIBLE;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY_VISIBLE);
    // If no value exists, default to open
    if (stored === null) {
      return CLIENT_DEFAULT_VISIBLE;
    }
    // Only stay closed if explicitly set to 'false'
    return stored !== 'false';
  } catch {
    return CLIENT_DEFAULT_VISIBLE;
  }
}

/**
 * Get stored highlight preference from localStorage
 * Returns true (on) by default
 */
function getStoredHighlight() {
  if (typeof window === 'undefined') {
    return DEFAULT_HIGHLIGHT;
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY_HIGHLIGHT);
    if (stored === null) {
      return DEFAULT_HIGHLIGHT;
    }
    return stored !== 'false';
  } catch {
    return DEFAULT_HIGHLIGHT;
  }
}

/**
 * Save highlight preference to localStorage
 */
function saveHighlight(highlight: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY_HIGHLIGHT, String(highlight));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Save visibility preference to localStorage
 */
function saveVisible(visible: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY_VISIBLE, String(visible));
  } catch {
    // Ignore storage errors
  }
}

function getEmoji(rating: Rating): string {
  if (rating === 'good') {
    return '✅';
  }
  if (rating === 'needs-improvement') {
    return '⚠️';
  }
  return '❌';
}

function getEnvBadgeColor() {
  const env = getCurrentEnv().toLowerCase();
  if (env === 'local') {
    return '#4a5568';
  }
  if (env === 'qa') {
    return '#9f7aea';
  }
  return '#3182ce';
}

const MetricBar = ({
  label,
  value,
  rating,
  target,
}: {
  label: string;
  value: string;
  rating: Rating;
  target: string;
}) => {
  return (
    <div
      style={{
        alignItems: 'center',
        background: '#2a2a3a',
        borderLeft: `3px solid ${colors[rating]}`,
        borderRadius: 4,
        display: 'flex',
        justifyContent: 'space-between',
        padding: '6px 10px',
      }}
    >
      <div>
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span style={{ color: '#666', fontSize: 10, marginLeft: 6 }}>({target})</span>
      </div>
      <div style={{ alignItems: 'center', display: 'flex', gap: 6 }}>
        <span style={{ color: colors[rating], fontWeight: 600 }}>{value}</span>
        <span>{getEmoji(rating)}</span>
      </div>
    </div>
  );
};

const CwvOverlayInner = () => {
  // Initialize as closed for SSR to avoid hydration mismatch
  const [visible, setVisible] = useState(SSR_DEFAULT_VISIBLE);
  const [highlightEnabled, setHighlightEnabled] = useState(DEFAULT_HIGHLIGHT);
  const [showInstructions, setShowInstructions] = useState(false);
  const [hasMounted, setHasMounted] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [data, setData] = useState<CwvData>({
    cls: 0,
    clsRating: 'good',
    inp: 0,
    inpRating: 'good',
    lcp: 0,
    lcpRating: 'good',
    shifts: [],
  });

  // Compute which metrics have issues (for unified notification bar)
  const issues: string[] = [];
  if (data.clsRating !== 'good') {
    issues.push(`CLS: ${data.cls.toFixed(3)} (${data.clsRating === 'poor' ? 'POOR' : 'needs work'})`);
  }
  if (data.inpRating !== 'good') {
    issues.push(`INP: ${Math.round(data.inp)}ms (${data.inpRating === 'poor' ? 'POOR' : 'needs work'})`);
  }
  // Only warn about LCP if not on local (LCP is expected to be slow locally)
  if (!isLocalEnv() && data.lcpRating !== 'good') {
    issues.push(`LCP: ${Math.round(data.lcp)}ms (${data.lcpRating === 'poor' ? 'POOR' : 'needs work'})`);
  }

  const hasWarnings = issues.length > 0;
  const hasPoorRating =
    data.clsRating === 'poor' || data.inpRating === 'poor' || (!isLocalEnv() && data.lcpRating === 'poor');

  // Reset dismissed state if issues change significantly (e.g., new poor rating)
  const previousHasPoor = useRef(hasPoorRating);
  useEffect(() => {
    if (hasPoorRating && !previousHasPoor.current) {
      setWarningDismissed(false); // Re-show warning when something becomes poor
    }
    previousHasPoor.current = hasPoorRating;
  }, [hasPoorRating]);

  // Load stored preferences after mount (client-side only) to avoid hydration mismatch
  useEffect(() => {
    setVisible(getStoredVisible());
    setHighlightEnabled(getStoredHighlight());
    setHasMounted(true);
  }, []);

  // Persist visibility changes to localStorage (only after initial mount)
  useEffect(() => {
    if (hasMounted) {
      saveVisible(visible);
    }
  }, [visible, hasMounted]);

  // Note: highlightEnabled is persisted directly in the toggle handler (with page reload)

  const highlightedRef = useRef<Set<Element>>(new Set());
  const highlightEnabledRef = useRef(highlightEnabled);

  // Keep ref in sync with state for use in callback
  useEffect(() => {
    highlightEnabledRef.current = highlightEnabled;
  }, [highlightEnabled]);

  // Highlight elements causing CLS
  const highlightElement = useCallback((node: Node | null) => {
    // Only highlight if the feature is enabled
    if (!highlightEnabledRef.current) {
      return;
    }

    if (!(node && node instanceof Element)) {
      return;
    }
    if (highlightedRef.current.has(node)) {
      return;
    }

    // Don't highlight the CWV overlay itself
    if ((node as HTMLElement).closest('[data-cwv-overlay]')) {
      return;
    }

    highlightedRef.current.add(node);
    const el = node as HTMLElement;
    const orig = el.style.outline;
    el.style.outline = '3px solid #ff4e42';

    // Keep highlight visible
    setTimeout(() => {
      el.style.outline = orig;
      highlightedRef.current.delete(node);
    }, clsHighlightDuration);
  }, []);

  useEffect(() => {
    onCLS(
      (metric: CLSMetricWithAttribution) => {
        const attribution = metric.attribution;
        const shifts =
          attribution?.largestShiftEntry?.sources?.map((s) => ({
            element: s.node?.nodeName || 'unknown',
            value: attribution.largestShiftValue || 0,
          })) || [];

        // Highlight shifting elements
        attribution?.largestShiftEntry?.sources?.forEach((source) => {
          highlightElement(source.node || null);
        });

        const rating = getRating('cls', metric.value);
        setData((prev) => ({
          ...prev,
          cls: metric.value,
          clsRating: rating,
          shifts,
        }));
      },
      { reportAllChanges: true },
    );

    onLCP(
      (metric: LCPMetricWithAttribution) => {
        setData((prev) => ({
          ...prev,
          lcp: metric.value,
          lcpRating: getRating('lcp', metric.value),
        }));
      },
      { reportAllChanges: true },
    );

    onINP(
      (metric: INPMetricWithAttribution) => {
        setData((prev) => ({
          ...prev,
          inp: metric.value,
          inpRating: getRating('inp', metric.value),
        }));
      },
      { reportAllChanges: true },
    );
  }, [highlightElement]);

  // Keyboard toggle: Ctrl+Shift+V
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'V') {
        e.preventDefault();
        setVisible((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Notification bar component (shown regardless of panel visibility)
  const notificationBar = hasWarnings && !warningDismissed && (
    <>
      <div
        data-cwv-overlay='true'
        style={{
          alignItems: 'center',
          animation: 'pulse 2s infinite',
          background: hasPoorRating ? '#ff4e42' : '#ffa400',
          borderRadius: 8,
          bottom: 16,
          boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
          color: '#fff',
          display: 'flex',
          fontFamily: 'ui-monospace, monospace',
          fontSize: 13,
          fontWeight: 600,
          gap: 12,
          left: '50%',
          padding: '12px 16px',
          position: 'fixed',
          transform: 'translateX(-50%)',
          zIndex: 100000,
        }}
      >
        <span style={{ fontSize: 20 }}>{hasPoorRating ? '🚨' : '⚠️'}</span>
        <div>
          <div style={{ marginBottom: 2 }}>CWV {hasPoorRating ? 'POOR' : 'Issues'} - Will hurt SEO!</div>
          <div style={{ fontSize: 11, fontWeight: 400, opacity: 0.95 }}>{issues.join(' • ')}</div>
        </div>
        <button
          aria-label='Dismiss warning'
          onClick={() => setWarningDismissed(true)}
          style={{
            background: 'rgba(255,255,255,0.2)',
            border: 'none',
            borderRadius: 4,
            color: '#fff',
            cursor: 'pointer',
            fontSize: 12,
            marginLeft: 8,
            padding: '4px 8px',
          }}
          type='button'
        >
          Dismiss
        </button>
      </div>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.85; }
          }
        `}
      </style>
    </>
  );

  if (!visible) {
    return (
      <>
        <button
          aria-label='Show CWV Overlay'
          data-cwv-overlay='true'
          onClick={() => setVisible(true)}
          style={{
            background: '#1e1e2e',
            border: 'none',
            borderRadius: '50%',
            boxShadow: '0 2px 12px rgba(0,0,0,0.4)',
            color: '#fff',
            cursor: 'pointer',
            fontSize: 18,
            height: 44,
            left: PANEL_POSITION.x,
            position: 'fixed',
            top: PANEL_POSITION.y,
            width: 44,
            zIndex: 99999,
          }}
          type='button'
        >
          📊
        </button>
        {notificationBar}
      </>
    );
  }

  return (
    <>
      <div
        aria-hidden='true'
        data-cwv-overlay='true'
        style={{
          background: '#1e1e2e',
          borderRadius: 8,
          boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
          color: '#e0e0e0',
          fontFamily: 'ui-monospace, monospace',
          fontSize: 12,
          left: PANEL_POSITION.x,
          minWidth: 200,
          overflow: 'hidden',
          position: 'fixed',
          top: PANEL_POSITION.y,
          zIndex: 99999,
        }}
      >
        {/* Header */}
        <div
          style={{
            alignItems: 'center',
            background: '#2d2d3d',
            display: 'flex',
            justifyContent: 'space-between',
            padding: '8px 12px',
            width: '100%',
          }}
        >
          <div style={{ alignItems: 'center', display: 'flex', gap: 6 }}>
            <span style={{ fontWeight: 600 }}>🔬 CWV</span>
            <span
              style={{
                background: getEnvBadgeColor(),
                borderRadius: 3,
                color: '#fff',
                fontSize: 9,
                padding: '2px 5px',
                textTransform: 'uppercase',
              }}
            >
              {getCurrentEnv()}
            </span>
          </div>
          <button
            aria-label='Close panel'
            onClick={() => setVisible(false)}
            style={{
              background: 'none',
              border: 'none',
              color: '#888',
              cursor: 'pointer',
              fontSize: 16,
              padding: '0 4px',
            }}
            type='button'
          >
            ×
          </button>
        </div>

        {/* Metrics */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: 10 }}>
          <MetricBar
            label='CLS'
            rating={data.clsRating}
            target='≤ 0.1'
            value={data.cls.toFixed(clsDisplayPrecision)}
          />
          <MetricBar
            label='LCP'
            rating={data.lcpRating}
            target='≤ 2500ms'
            value={`${Math.round(data.lcp)}ms`}
          />
          <MetricBar
            label='INP'
            rating={data.inpRating}
            target='≤ 200ms'
            value={data.inp ? `${Math.round(data.inp)}ms` : '—'}
          />

          {/* CLS Highlight Toggle */}
          <button
            onClick={() => {
              const newValue = !highlightEnabled;
              saveHighlight(newValue);
              window.location.reload();
            }}
            style={{
              alignItems: 'center',
              background: '#2a2a3a',
              border: 'none',
              borderRadius: 4,
              color: highlightEnabled ? '#e0e0e0' : '#666',
              cursor: 'pointer',
              display: 'flex',
              fontSize: 10,
              gap: 8,
              justifyContent: 'space-between',
              marginTop: 4,
              padding: '6px 8px',
              width: '100%',
            }}
            title={highlightEnabled ? 'Click to disable CLS highlighting' : 'Click to enable CLS highlighting'}
            type='button'
          >
            <span>🔴 Highlight CLS shifts</span>
            <span
              style={{
                background: highlightEnabled ? '#0cce6b' : '#4a5568',
                borderRadius: 10,
                display: 'inline-block',
                height: 14,
                position: 'relative',
                transition: 'background 0.2s',
                width: 26,
              }}
            >
              <span
                style={{
                  background: '#fff',
                  borderRadius: '50%',
                  display: 'block',
                  height: 10,
                  left: highlightEnabled ? 14 : 2,
                  position: 'absolute',
                  top: 2,
                  transition: 'left 0.2s',
                  width: 10,
                }}
              />
            </span>
          </button>

          {/* Local LCP warning */}
          {isLocalEnv() && data.lcpRating !== 'good' && (
            <div
              style={{
                background: '#2a2a3a',
                borderRadius: 4,
                color: '#a0aec0',
                fontSize: 10,
                marginTop: 2,
                padding: '6px 8px',
              }}
            >
              💡 LCP is expected to be slow locally. Check DEV/QA for real values.
            </div>
          )}

          {/* Mobile testing reminder */}
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            style={{
              background: '#2a2a3a',
              border: 'none',
              borderRadius: 4,
              color: '#718096',
              cursor: 'pointer',
              fontSize: 10,
              marginTop: 4,
              padding: '6px 8px',
              textAlign: 'left',
              width: '100%',
            }}
            title='Click for instructions'
            type='button'
          >
            📱 Test mobile? {showInstructions ? '▼' : '→'}
          </button>

          {showInstructions && (
            <div
              style={{
                background: '#2a2a3a',
                borderRadius: 4,
                color: '#a0aec0',
                fontSize: 10,
                lineHeight: 1.4,
                padding: '8px',
              }}
            >
              <strong>Simulate mobile/slow connection:</strong>
              <br />
              1. Open DevTools (F12)
              <br />
              2. Go to Network tab
              <br />
              3. Select &quot;Slow 4G&quot;
              <br />
              4. Enable device toolbar (CMD+Shift+M)
              <br />
              <br />
              <em>CLS issues are often worse on slow connections!</em>
            </div>
          )}
        </div>
      </div>

      {/* Unified CWV Warning Notification Bar */}
      {notificationBar}
    </>
  );
};

export const CwvOverlay = () => {
  // Only render in local dev, DEV, or QA environments
  if (!shouldShowOverlay()) {
    return null;
  }

  return <CwvOverlayInner />;
};
